import { essayAPI } from './api';

// 进度状态接口
export interface StageStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
}

// 阶段详情接口
export interface StageDetails {
  upload: StageStatus;
  ocr: StageStatus;
  analysis: StageStatus;
  generation: StageStatus;
}

// 进度信息接口
export interface ProgressInfo {
  essay_id: number;
  stage: 'upload' | 'ocr' | 'analyzing' | 'generating' | 'completed';
  progress: number;
  status: 'processing' | 'completed' | 'failed';
  message: string;
  details: StageDetails;
  start_time: string;
  estimated_time?: number;
  updated_at: string;
}

// 进度更新回调类型
type ProgressCallback = (progress: ProgressInfo) => void;
type ErrorCallback = (error: Error) => void;

// 进度管理器类
export class ProgressManager {
  private pollTimer?: NodeJS.Timeout;
  private essayId?: number;
  private callbacks: ProgressCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];
  private currentProgress?: ProgressInfo;
  private pollInterval: number = 1000; // 默认1秒轮询一次
  private maxRetries: number = 3;
  private retryCount: number = 0;

  // 开始轮询进度
  async startPolling(
    essayId: number,
    options?: {
      interval?: number;
      immediate?: boolean;
    }
  ): Promise<void> {
    this.essayId = essayId;
    this.retryCount = 0;

    if (options?.interval) {
      this.pollInterval = options.interval;
    }

    // 清理之前的轮询
    this.stopPolling();

    // 立即执行一次
    if (options?.immediate !== false) {
      await this.fetchProgress();
    }

    // 设置定时轮询
    this.pollTimer = setInterval(async () => {
      await this.fetchProgress();
    }, this.pollInterval);
  }

  // 获取进度信息
  private async fetchProgress(): Promise<void> {
    if (!this.essayId) return;

    try {
      const response = await essayAPI.getProgress(this.essayId);
      const progress = response.data as ProgressInfo;
      
      this.currentProgress = progress;
      this.retryCount = 0; // 重置重试计数
      
      // 通知所有监听器
      this.callbacks.forEach(callback => callback(progress));

      // 如果已完成或失败，停止轮询
      if (progress.status === 'completed' || progress.status === 'failed') {
        this.stopPolling();
      }
    } catch (error) {
      this.retryCount++;
      
      // 如果超过最大重试次数，停止轮询并通知错误
      if (this.retryCount >= this.maxRetries) {
        this.stopPolling();
        this.errorCallbacks.forEach(callback => callback(error as Error));
      }
    }
  }

  // 停止轮询
  stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  // 注册进度更新回调
  onProgress(callback: ProgressCallback): void {
    this.callbacks.push(callback);
  }

  // 注册错误回调
  onError(callback: ErrorCallback): void {
    this.errorCallbacks.push(callback);
  }

  // 移除进度回调
  offProgress(callback: ProgressCallback): void {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }
  }

  // 移除错误回调
  offError(callback: ErrorCallback): void {
    const index = this.errorCallbacks.indexOf(callback);
    if (index > -1) {
      this.errorCallbacks.splice(index, 1);
    }
  }

  // 清理所有回调
  clearCallbacks(): void {
    this.callbacks = [];
    this.errorCallbacks = [];
  }

  // 获取当前进度
  getCurrentProgress(): ProgressInfo | undefined {
    return this.currentProgress;
  }

  // 获取轮询状态
  isPolling(): boolean {
    return !!this.pollTimer;
  }

  // 设置轮询间隔
  setInterval(interval: number): void {
    this.pollInterval = interval;
    
    // 如果正在轮询，重新启动
    if (this.isPolling() && this.essayId) {
      this.startPolling(this.essayId, { interval });
    }
  }

  // 销毁管理器
  destroy(): void {
    this.stopPolling();
    this.clearCallbacks();
    this.currentProgress = undefined;
    this.essayId = undefined;
  }
}

// 导出单例
export const progressManager = new ProgressManager();

// 辅助函数：获取阶段的中文名称
export function getStageName(stage: string): string {
  const stageMap: Record<string, string> = {
    upload: '文件上传',
    ocr: 'OCR识别',
    analyzing: 'AI分析',
    generating: '生成报告',
    completed: '完成',
  };
  return stageMap[stage] || stage;
}

// 辅助函数：获取状态的颜色
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    pending: '#d9d9d9',
    processing: '#1890ff',
    completed: '#52c41a',
    failed: '#ff4d4f',
  };
  return colorMap[status] || '#d9d9d9';
}

// 辅助函数：格式化剩余时间
export function formatEstimatedTime(seconds?: number): string {
  if (!seconds || seconds <= 0) {
    return '';
  }
  
  if (seconds < 60) {
    return `约 ${seconds} 秒`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (remainingSeconds === 0) {
    return `约 ${minutes} 分钟`;
  }
  
  return `约 ${minutes} 分 ${remainingSeconds} 秒`;
}